import { useEffect, useState } from 'react';
import { leaderboardService } from '../services/leaderboard.service';
import { LeaderboardRow } from '../components/leaderboard/LeaderboardRow';
import { LeagueCard } from '../components/leaderboard/LeagueCard';
import { useAuthStore } from '../store/authStore';
import { LeaderboardEntry, LeaderboardScope } from '../types/leaderboard.types';
import { supabase } from '../lib/supabaseClient';
import '../styles/leaderboard.css';

const SCOPE_LABEL:Record<LeaderboardScope,string>={week:'Bu Hafta',month:'Bu Ay',all:'Tüm Zamanlar'};
const AGE_GROUPS=['Tümü','6-8 Yaş','9-11 Yaş','12-14 Yaş','15-18 Yaş'];
const AWARD_CATS=[
  {key:'xp',icon:'🏆',label:'En Çok XP'},
  {key:'quiz',icon:'🧠',label:'En Çok Quiz'},
  {key:'project',icon:'💻',label:'En Çok Proje'},
  {key:'streak',icon:'🔥',label:'En Uzun Seri'},
  {key:'python',icon:'🐍',label:'Python Ustası'},
  {key:'matematik',icon:'🧮',label:'Matematik Şampiyonu'},
  {key:'ingilizce',icon:'🌍',label:'İngilizce Kahramanı'},
  {key:'fen',icon:'🧪',label:'Bilim Kaşifi'},
];

export function LeaderboardPage() {
  const [scope, setScope] = useState<LeaderboardScope>('week');
  const [ageGroup, setAgeGroup] = useState('Tümü');
  const [awardCat, setAwardCat] = useState('xp');
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const profile = useAuthStore((s) => s.profile);
  const [isLoading, setIsLoading] = useState(true);
  const [seasonName, setSeasonName] = useState('');

  const currentXp = profile?.league_xp ?? 0;

  useEffect(() => {
    supabase
      .from('leaderboard_seasons')
      .select('name')
      .eq('is_active', true)
      .maybeSingle()
      .then(({ data }) => data && setSeasonName(data.name));
  }, []);

  useEffect(()=>{
    setIsLoading(true);
    leaderboardService.getLeaderboard(scope)
      .then(data=>{
        const filtered = ageGroup==='Tümü'?data:data.filter((e:any)=>e.ageGroup===ageGroup);
        setEntries(filtered);
      })
      .catch(()=>setEntries([]))
      .finally(()=>setIsLoading(false));
  },[scope,ageGroup]);

  const top3=entries.slice(0,3);

  return (
    <div className="leaderboard-page">
      <header className="leaderboard-header">
        <h1>🏆 Liderlik Tablosu</h1>
        <div className="leaderboard-header-meta">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Lig XP:</span>
            <div className="league-xp-pill" style={{ padding: '4px 12px' }}>
              <span className="xp-lightning-icon" style={{ fontSize: 14 }}>⚡</span>
              <span className="xp-value" style={{ fontSize: 15 }}>{currentXp.toLocaleString('tr-TR')}</span>
              <span className="xp-label" style={{ fontSize: 10 }}>XP</span>
            </div>
          </div>
          <p className="leaderboard-meta-note">Coin harcayıp normal XP harcadığınızda lig XP'niz etkilenmez; lig skorunuz yalnızca aylık sezon sıfırlamasıyla güncellenir.</p>
        </div>
        {seasonName && <div className="season-badge">🗓️ {seasonName} — Her ay sıfırlanır!</div>}
      </header>

      <LeagueCard />

      <div className="card lb-filters">
        <div className="filter-row">
          <span className="filter-label">⏱</span>
          {(Object.keys(SCOPE_LABEL) as LeaderboardScope[]).map(s=>(
            <button key={s} className={`courses-tab ${scope===s?'active':''}`} onClick={()=>setScope(s)}>{SCOPE_LABEL[s]}</button>
          ))}
        </div>
        <div className="filter-row" style={{marginTop:10}}>
          <span className="filter-label">👶</span>
          {AGE_GROUPS.map(ag=>(
            <button key={ag} className={`courses-tab ${ageGroup===ag?'active':''}`} onClick={()=>setAgeGroup(ag)}>{ag}</button>
          ))}
        </div>
      </div>

      <div className="card award-cats">
        <h3 className="parent-section-title">🎖️ Ödül Kategorileri</h3>
        <div className="award-cat-grid">
          {AWARD_CATS.map(cat=>(
            <button key={cat.key} className={`award-cat-btn ${awardCat===cat.key?'active':''}`} onClick={()=>setAwardCat(cat.key)}>
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Podyum */}
      {!isLoading&&top3.length>0&&(
        <div className="lb-podium">
          {[top3[1],top3[0],top3[2]].filter(Boolean).map(entry=>{
            if(!entry) return null;
            const medals=['🥇','🥈','🥉'];
            const ht=entry.rank===1?80:entry.rank===2?60:40;
            return (
              <div key={entry.rank} className="podium-slot">
                <div className="podium-medal">{medals[entry.rank-1]}</div>
                <div className="podium-name">{entry.displayName}</div>
                {entry.schoolName && <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>{entry.schoolName}</div>}
                <div className="podium-xp">{entry.xp?.toLocaleString('tr-TR')} XP</div>
                <div className="podium-base" style={{height:ht,background:entry.rank===1?'#F59E0B':entry.rank===2?'#94A3B8':'#CD7F32'}}/>
              </div>
            );
          })}
        </div>
      )}

      <div className="card">
        <h3 className="parent-section-title">Tam Sıralama</h3>
        {isLoading&&<div className="courses-loading">Yükleniyor...</div>}
        {!isLoading&&entries.length===0&&<p className="parent-empty-text">Henüz veri yok.</p>}
        <div className="leaderboard-list">
          {entries.map(e=><LeaderboardRow key={e.rank} entry={e}/>)}
        </div>
      </div>

      <div className="card scoring-box">
        <h3 className="parent-section-title">📊 Puan Sistemi (Hile Önleme)</h3>
        <div className="scoring-grid">
          {[{i:'📖',l:'Ders (ilk izleme)',p:'10 puan'},{i:'✅',l:'Quiz (ilk başarı)',p:'30 puan'},{i:'⚡',l:'Challenge',p:'40 puan'},{i:'🎯',l:'Mini Proje',p:'100 puan'},{i:'🔥',l:'7 Gün Seri',p:'70 puan'}].map(s=>(
            <div key={s.l} className="scoring-item"><span>{s.i}</span><div><div style={{fontWeight:700}}>{s.l}</div><div style={{color:'var(--gray)',fontSize:12}}>{s.p}</div></div></div>
          ))}
        </div>
        <p className="parent-empty-text" style={{marginTop:12}}>⚠️ Aynı dersi tekrar izlemek puan vermez. Quiz'den yalnızca ilk başarılı denemede tam puan.</p>
      </div>
    </div>
  );
}
